import { Repeat } from "lucide-react";
import { Button } from "../ui/button";
import type { Id } from "convex/_generated/dataModel";

export type RecurringAction = "edit" | "approve" | "reject" | "archive";

interface RecurringActionDialogProps {
  action: RecurringAction;
  patternId: Id<"recurringPatterns">;
  onClose: () => void;
  /** Called when user chooses "this event only" */
  onThisEvent: () => void;
  /** Called when user chooses "all events in series" */
  onAllEvents: () => void;
}

const ACTION_LABELS: Record<RecurringAction, string> = {
  edit: "Edit",
  approve: "Approve",
  reject: "Reject",
  archive: "Archive",
};

export function RecurringActionDialog({
  action,
  onClose,
  onThisEvent,
  onAllEvents,
}: RecurringActionDialogProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl p-6 mx-4 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-1">
          <Repeat className="w-4 h-4 text-primary" />
          <h2 className="font-semibold text-foreground">{ACTION_LABELS[action]} recurring event</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          This is part of a recurring series. What would you like to {action}?
        </p>

        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            className="justify-start h-auto py-3 px-4"
            onClick={onThisEvent}
          >
            <span className="w-4 h-4 mr-2 text-center text-xs font-bold">1</span>
            <div className="text-left">
              <div className="font-medium">This event only</div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="justify-start h-auto py-3 px-4"
            onClick={onAllEvents}
          >
            <Repeat className="w-4 h-4 mr-2" />
            <div className="text-left">
              <div className="font-medium">All events in series</div>
              <div className="text-xs text-muted-foreground">Affects the entire recurring pattern</div>
            </div>
          </Button>

          <Button variant="ghost" className="mt-1" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
